import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  Button,
  Input,
  SearchIcon,
  Tabs,
  ITabOption,
  Select,
  IncomeIcon,
  Tooltip,
  TelegramBellIcon,
} from '@smartshell/ui-kit';
import { useNavigate } from 'react-router-dom';

import { FeatureHOC, useIsFeatureAvailable } from 'features';
import { useCategoryRequests, useGoodsRequests, useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { AmountReportModal, ManagerWarehouseModal } from 'modals';
import { SettingsContext } from 'settings';
import { TGood, TGroup } from 'types';
import { debounce } from 'utils';

import s from './Goods.module.scss';
import { GoodsGoodTable } from './GoodsGoodTable';
import { GoodsGroupTable } from './GoodsGroupTable';
import { GroupManager } from './GroupCreate';
import { NotificationDelete } from './NotificationDelete';
import { TelegramModal } from './TelegramModal';

export const Goods: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);
  const navigate = useNavigate();
  const isGoodsExportAvailable = useIsFeatureAvailable('export_goods');
  const isTelegramAvailable = useIsFeatureAvailable('export_goods');

  const [searchStr, setSearchStr] = useState('');
  const [groupFiltered, setGroupFiltered] = useState<null | string>(null);
  const [isDeletingError, setIsDeletingError] = useState(false);
  const [deletingMessage, setDeletingMessage] = useState<string>('');

  const {
    loadedGoods,
    isGoodsLoading,
    exportGoods,
    refetchGoods,
    updateGoods,
  } = useGoodsRequests({ setIsDeletingError, setDeletingMessage });

  const {
    loadedCategories,
    isCategoryLoading,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useCategoryRequests({ setIsDeletingError });

  const [selectedGood, setSelectedGood] = useState<TGood | null>(null);
  const [goofForAmountChange, setGoodForAmountChange] = useState<TGood | null>(
    null
  );
  const [isGroupManager, setIsGroupManager] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<TGroup | null>(null);
  const [amountBalances, setAmountBalances] = useState<number>(0);
  const [telegramModal, setTelegramModal] = useState<boolean>(false);
  const [amountReport, setAmountReport] = useState<TGood | null>(null);

  const editGroup = (group: TGroup): void => {
    setIsGroupManager(true);
    setSelectedGroup(group);
  };

  const isFirstRender = useRef(true);

  useEffect(() => {
    const currentGood = loadedGoods.find(
      (good) => good?.id === selectedGood?.id
    );
    if (currentGood && currentGood !== selectedGood) {
      setSelectedGood(currentGood);
    }
    const currentGroup = loadedCategories.find(
      (group) => group?.id === selectedGroup?.id
    );
    if (currentGroup && currentGroup !== selectedGroup) {
      setSelectedGroup(currentGroup);
    }
  }, [loadedGoods, loadedCategories, selectedGroup]);

  const debouncedRefetchGoods = useMemo(
    () => debounce(refetchGoods, 500),
    [refetchGoods]
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const length = searchStr.length;
    if (length !== 0 && length < 2) {
      return;
    }

    debouncedRefetchGoods({
      input: {
        title_search: searchStr || undefined,
        category_id:
          groupFiltered === 'empty'
            ? 0
            : groupFiltered !== null
            ? groupFiltered
            : undefined,
      },
    });
  }, [searchStr, groupFiltered, debouncedRefetchGoods]);

  enum EGoodsTabs {
    GOODS = 'GOODS',
    GROUP = 'GROUP',
  }
  const TABS: Array<ITabOption<EGoodsTabs>> = [
    {
      title: t('category.goods'),
      value: EGoodsTabs.GOODS,
      count: loadedGoods.length,
    },
    {
      title: t('groups'),
      value: EGoodsTabs.GROUP,
      count: loadedCategories.length,
    },
  ];
  const [tab, setTab] = useState<EGoodsTabs>(EGoodsTabs.GOODS);

  const size = useWindowResize();
  return (
    <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      {telegramModal && (
        <TelegramModal
          loadedGoods={loadedGoods}
          updateGoods={updateGoods}
          onClose={() => setTelegramModal(false)}
        />
      )}

      {isGroupManager && (
        <GroupManager
          isCategoryLoading={isCategoryLoading}
          group={selectedGroup}
          createCategory={createCategory}
          updateCategory={updateCategory}
          deleteCategory={deleteCategory}
          onClose={() => {
            if (selectedGroup) {
              setSelectedGroup(null);
            }
            setIsGroupManager(false);
          }}
        />
      )}
      {isDeletingError && (
        <NotificationDelete
          title={
            tab === 'GOODS'
              ? t('good.deleting')
              : t('accounts.group.delete.confirm')
          }
          description={
            tab === 'GOODS' ? deletingMessage : t('group.delete.error.message')
          }
          onClose={() => setIsDeletingError(false)}
          showButton={true}
        />
      )}
      {goofForAmountChange && (
        <ManagerWarehouseModal
          good={goofForAmountChange}
          onClose={() => setGoodForAmountChange(null)}
        />
      )}
      {amountReport && (
        <AmountReportModal
          good={amountReport}
          onClose={() => setAmountReport(null)}
        />
      )}

      <div className={s['page-container']}>
        <div className={s.controls}>
          <div className={s.tabs_block}>
            <Tabs
              options={TABS}
              onChange={setTab}
              itemVariant={'row'}
              value={tab}
            />
          </div>
          {tab === 'GOODS' && (
            <>
              <Input
                variant="secondary"
                value={searchStr}
                onChange={setSearchStr}
                placeholder={t('goods.search')}
                leftContent={<SearchIcon color="primary" />}
                className={s['search-input']}
                errorWrapperClassName={s.searchInput}
              />
              {loadedCategories.length !== 0 && (
                <Select
                  name={'group-filtered'}
                  placeholder={t('groups.all')}
                  dropdownsClassNames={s.select_group_dropdown}
                  emptyOptionsPlaceholder={t('goods.select.empty_options')}
                  options={[
                    ...loadedCategories.map((category) => ({
                      title: category.title,
                      value: category?.id,
                    })),
                    {
                      title: t('goods.table.category_empty'),
                      value: 'empty',
                    },
                  ]}
                  className={s.select_group}
                  value={groupFiltered}
                  onChange={(newValue) => {
                    if (newValue) {
                      setGroupFiltered(newValue);
                    } else {
                      setGroupFiltered(null);
                    }
                  }}
                  disableClear={false}
                  errorWrapperClassName={s.selectGroup}
                />
              )}

              {isTelegramAvailable &&
                (size.width > 960 ? (
                  <Tooltip
                    content={t('goods.header.telegram.tooltip')}
                    contentClassName={s.tooltip_content}
                    className={s.tooltipContent}
                    position={size.width > 1864 ? 'bottom' : 'bottom-left'}
                  >
                    <button
                      className={s.telegram_button}
                      onClick={() => setTelegramModal(true)}
                    >
                      <TelegramBellIcon size="m" color={'secondary'} />
                    </button>
                  </Tooltip>
                ) : (
                  <button
                    className={s.telegram_button}
                    onClick={() => setTelegramModal(true)}
                  >
                    <TelegramBellIcon size="m" color={'secondary'} />
                  </button>
                ))}

              <div className={s.balance}>
                <span className={s.balance_text}>
                  {t('goods.header.balance_amount')}
                </span>
                <span className={s.balance_sum}>
                  {amountBalances.toLocaleString() + ' ' + currency.sign}
                </span>
              </div>
              <div className={s.button_export}>
                <FeatureHOC
                  name={t('export.csv')}
                  alias="export_goods"
                  strategy="disable"
                  tooltipPosition="bottom"
                >
                  <Button
                    disabled={
                      !isGoodsExportAvailable || loadedGoods.length === 0
                    }
                    variant="outline"
                    className={s.button}
                    onClick={() => {
                      const group =
                        groupFiltered === 'empty'
                          ? 0
                          : groupFiltered !== null
                          ? groupFiltered
                          : undefined;
                      exportGoods(searchStr, group);
                    }}
                  >
                    <IncomeIcon
                      className={s.button_icon}
                      color={
                        isGoodsExportAvailable && loadedGoods.length !== 0
                          ? 'link-info'
                          : 'disable'
                      }
                    />
                    <span className={s.button_text}>{t('export.csv')}</span>
                  </Button>
                </FeatureHOC>
              </div>
            </>
          )}
          {tab === 'GOODS' ? (
            <Button
              className={s.add_button}
              onClick={() => {
                setSelectedGood(null);
                navigate('/goods/new', { replace: true });
              }}
              iconName="CirclePlusIcon"
            >
              {t('goods.add')}
            </Button>
          ) : (
            <Button
              onClick={() => {
                setIsGroupManager(true);
              }}
              className={s.add_group}
            >
              {t('applications.groups.create.add')}
            </Button>
          )}
        </div>
        {tab === 'GOODS' && (
          <GoodsGoodTable
            loadedCategories={loadedCategories}
            filteredGoods={loadedGoods}
            finishGoodEdit={() => {
              if (selectedGood) {
                setSelectedGood(null);
              }
            }}
            setAmountBalances={setAmountBalances}
            openAmountEditor={setGoodForAmountChange}
            groupFiltered={groupFiltered}
            setIsDeletingError={setIsDeletingError}
            loadingTable={isGoodsLoading}
            setDeletingMessage={setDeletingMessage}
            setAmountReport={setAmountReport}
          />
        )}
        {tab === 'GROUP' && (
          <GoodsGroupTable
            loadedCategories={loadedCategories}
            editGroup={editGroup}
            deleteCategory={deleteCategory}
            updateCategory={updateCategory}
            setIsDeletingError={setIsDeletingError}
            loadingTable={isGoodsLoading || isCategoryLoading}
          />
        )}
      </div>
    </div>
  );
};
